import { toRaw } from 'vue'
import { createToast, withProps, clearToasts } from 'mosha-vue-toastify'
import { vsToast } from '@isagaco/vuesaga'
import { v4 as uuidv4 } from 'uuid'
import { client as hasuraClient } from '@/services/hasura'
import { useAuthStore } from '@/stores'

// Direct writes under the caller's Hasura role (replaces the admin /mutation route).
// Takes the same descriptors as methods.mutation and builds the equivalent GraphQL document
// client-side. Values always travel as variables; only identifiers from code are written
// into the query text, and those are validated.

type Row = Record<string, any>

export interface MutationDescriptor {
  action: 'insert' | 'update' | 'delete' | 'updateMany'
  table: string | string[]
  schema?: string
  // insert: row or rows. update: columns to set. delete: primary key columns.
  // updateMany: [{ pkey, data }]
  data: any
  // update: primary key columns, e.g. { id }
  pkey?: Row
  returnKeys?: string | string[]
  // insert: on_conflict update_columns. Defaults to the provided keys minus id and instanceID
  // (no role can update instanceID).
  updateColumns?: string[]
  // insert: false drops on_conflict, for roles without an update permission
  upsert?: boolean
}

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/
const assertIdentifier = (value: string) => {
  if (!IDENTIFIER.test(value)) throw new Error(`Invalid GraphQL identifier: ${value}`)
  return value
}

// Same normalization axios applied: Dates to ISO strings, undefined keys dropped, proxies unwrapped
const plain = (value: any) => (value === undefined ? undefined : JSON.parse(JSON.stringify(toRaw(value))))

// '$jsonbN' placeholders take the Nth jsonb value
const fillJsonb = (value: any, jsonb: any[]): any => {
  if (typeof value === 'string') {
    const match = value.match(/^\$jsonb(\d+)$/)
    return match && Number(match[1]) < jsonb.length ? jsonb[Number(match[1])] : value
  }
  if (Array.isArray(value)) return value.map((item) => fillJsonb(item, jsonb))
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, fillJsonb(item, jsonb)]))
  }
  return value
}

// Placeholder ids (e.g. '_tripID') become one generated uuid reused across the call
const fillPlaceholders = (value: any, placeholders: Record<string, string>) => {
  if (value === undefined || !Object.keys(placeholders).length) return value
  let text = JSON.stringify(value)
  for (const [name, uuid] of Object.entries(placeholders)) {
    text = text.split(name).join(uuid)
  }
  return JSON.parse(text)
}

const selection = (keys: string[]) => keys.map(assertIdentifier).join(' ')
const toKeys = (returnKeys?: string | string[]) =>
  !returnKeys ? [] : Array.isArray(returnKeys) ? returnKeys : returnKeys.split(/[\s,]+/).filter(Boolean)
// A missing key value must fail like the backend's *_by_pk did, never widen the where to every
// row. Checked before normalization, which drops undefined keys.
const assertKeyValues = (pkey: Row) => {
  const values = Object.values(pkey || {})
  if (!values.length || values.some((value) => value === null || value === undefined)) {
    throw new Error('Primary key value missing')
  }
}
const whereFromPkey = (pkey: Row) =>
  Object.fromEntries(Object.entries(pkey).map(([key, value]) => [assertIdentifier(key), { _eq: value }]))

// Actor columns Hasura stamps from the session (insert presets). Never sent by the client.
const SERVER_STAMPED: Record<string, string[]> = {
  events: ['userID'],
  tokens: ['userID'],
  vehicleChecks: ['userID'],
  tripRatings: ['userID'],
  cancelations: ['userID'],
  notifications: ['userID'],
  optimizations: ['userID'],
  userActivity: ['userID'],
  user_favorite_routes: ['userID'],
  routeModifications: ['createdBy', 'updatedBy', 'deletedBy'],
  billing_chargeNotifications: ['userID'],
  billing_pricingPlans: ['createdBy', 'updatedBy'],
}
const stripStamped = (name: string, row: any) => {
  const drop = SERVER_STAMPED[name]
  if (!drop || !row || typeof row !== 'object') return row
  const out = { ...row }
  for (const key of drop) delete out[key]
  return out
}

export const buildMutation = (mutations: MutationDescriptor[] | Row[], jsonb?: any[] | null, placeholderNames?: string[] | null) => {
  const jsonbValues = (jsonb || []).map(plain)
  const placeholders = Object.fromEntries((placeholderNames || []).map((name) => [name, uuidv4()]))
  const definitions: string[] = []
  const fields: string[] = []
  const variables: Row = {}
  const fieldNames: string[] = []
  const descriptors = mutations as MutationDescriptor[]
  descriptors.forEach((mutation, index) => {
    const table = assertIdentifier(String(mutation.table))
    const name = mutation.schema && mutation.schema !== 'public' ? `${assertIdentifier(mutation.schema)}_${table}` : table
    const alias = `m${index}`
    const raw = fillPlaceholders(plain(mutation.data), placeholders)
    if (mutation.action === 'insert') {
      const objects = (Array.isArray(raw) ? raw : [raw]).map((row) => stripStamped(name, fillJsonb(row, jsonbValues)))
      const updateColumns = mutation.updateColumns ||
        Object.keys(objects[0] || {}).filter((key) => key !== 'id' && key !== 'instanceID')
      definitions.push(`$o${index}: [${name}_insert_input!]!`)
      variables[`o${index}`] = objects
      let onConflict = ''
      if (mutation.upsert !== false) {
        definitions.push(`$u${index}: [${name}_update_column!]!`)
        variables[`u${index}`] = updateColumns.map(assertIdentifier)
        onConflict = `, on_conflict: {constraint: ${table}_pkey, update_columns: $u${index}}`
      }
      const returning = toKeys(mutation.returnKeys)
      fields.push(`${alias}: insert_${name}(objects: $o${index}${onConflict}) { affected_rows ${returning.length ? `returning { ${selection(returning)} }` : ''} }`)
      fieldNames.push(`insert_${name}`)
    } else if (mutation.action === 'update') {
      assertKeyValues(mutation.pkey)
      // Like the backend: any jsonb placeholder turns the whole update into _append
      const operator = JSON.stringify(raw).includes('jsonb') ? '_append' : '_set'
      definitions.push(`$p${index}: ${name}_pk_columns_input!`, `$s${index}: ${name}${operator}_input!`)
      variables[`p${index}`] = plain(mutation.pkey)
      variables[`s${index}`] = fillJsonb(raw, jsonbValues)
      const returnKeys = toKeys(mutation.returnKeys)
      fields.push(`${alias}: update_${name}_by_pk(pk_columns: $p${index}, ${operator}: $s${index}) { ${selection(returnKeys.length ? returnKeys : Object.keys(mutation.pkey))} }`)
      fieldNames.push(`update_${name}_by_pk`)
    } else if (mutation.action === 'delete') {
      // delete by primary key, as a where on every key column
      assertKeyValues(mutation.data)
      definitions.push(`$w${index}: ${name}_bool_exp!`)
      variables[`w${index}`] = whereFromPkey(raw)
      fields.push(`${alias}: delete_${name}(where: $w${index}) { affected_rows }`)
      fieldNames.push(`delete_${name}_by_pk`)
    } else if (mutation.action === 'updateMany') {
      // Several by-pk updates in one statement (rows the role may update but not insert)
      mutation.data.forEach((item: Row) => assertKeyValues(item.pkey))
      definitions.push(`$n${index}: [${name}_updates!]!`)
      variables[`n${index}`] = raw.map((item: Row) => ({ where: whereFromPkey(item.pkey), _set: fillJsonb(item.data, jsonbValues) }))
      fields.push(`${alias}: update_${name}_many(updates: $n${index}) { affected_rows }`)
      fieldNames.push(`update_${name}_many`)
    } else {
      throw new Error(`Unknown mutation action: ${(mutation as any).action}`)
    }
  })
  const query = `mutation urqlMutation(${definitions.join(', ')}) { ${fields.join(' ')} }`
  return { query, variables, fieldNames }
}

// The role a parent screen writes as: 'user' when the account has it, else the default role
export const parentRole = () => {
  const authStore = useAuthStore()
  return authStore.roles.includes('user') ? 'user' : undefined
}

// Mirrors methods.mutation: same arguments, loading/success/error toasts, logs and rethrows on
// error, and resolves to { code, message, mutationsMetadata } with message keyed by the backend's
// field names. role sets x-hasura-role for this operation; omitted, Hasura uses the JWT default.
export const urqlMutation = async (
  mutations: MutationDescriptor[] | Row[],
  jsonb?: any[] | null,
  variables?: string[] | null,
  noToast?: boolean,
  role?: string,
) => {
  try {
    if (!noToast) {
      createToast(withProps(vsToast, { type: 'loading', text: 'Acción en progreso' }), { hideProgressBar: true, timeout: 100000})
    }
    const { query, variables: queryVariables, fieldNames } = buildMutation(mutations, jsonb, variables)
    const context = role ? { fetchOptions: { headers: { 'x-hasura-role': role } } } : undefined
    const { data, error } = await hasuraClient.mutation(query, queryVariables, context).toPromise()
    if (error) {
      console.error(error)
      let errorMessage
      if (error.graphQLErrors?.[0]?.message?.includes('Uniqueness violation')) {
        errorMessage = 'Ya existe un registro con la información ingresada'
      }
      throw new Error(errorMessage || 'Error editando la información')
    }
    if (!noToast) {
      clearToasts()
      createToast(withProps(vsToast, { type: 'success', text: 'Acción realizada' }), { hideProgressBar: true, timeout: 3000})
    }
    const message = Object.fromEntries(fieldNames.map((fieldName, index) => [fieldName, data?.[`m${index}`]]))
    const mutationsMetadata = (mutations as MutationDescriptor[]).map(({ action, table, schema }) => ({ action, table, schema }))
    return { code: 200, message, mutationsMetadata }
  } catch (err) {
    if (!noToast) {
      clearToasts()
      createToast(withProps(vsToast, { type: 'error', text: err.message || 'No se pudo completar la acción' }), { hideProgressBar: true, timeout: 3000 })
    }
    console.error(err)
    throw(err)
  }
}

export default urqlMutation
